import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import { useCart } from "@/contexts/CartContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const HERO_IMG = "https://images.unsplash.com/photo-1542219550-37153d387c27?w=1600&q=80";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_food-cart-pro-3/artifacts/qa8o24lz_06B904DE-5CF2-4C4D-9FF1-6DCC2AB7D252.png";

export default function Home() {
  const { addItem } = useCart();
  const [data, setData] = useState({ specials: [], base_drinks: [], flavors: [] });
  const [loading, setLoading] = useState(true);

  // builder state
  const [drinkType, setDrinkType] = useState(null);
  const [sizeId, setSizeId] = useState(null);
  const [selectedFlavors, setSelectedFlavors] = useState([]);

  useEffect(() => {
    api.get("/menu").then((r) => {
      setData(r.data);
      const types = [...new Set(r.data.base_drinks.map((b) => b.drink_type))];
      if (types[0]) setDrinkType(types[0]);
    }).catch(() => toast.error("Could not load menu")).finally(() => setLoading(false));
  }, []);

  const drinkTypes = [...new Set(data.base_drinks.map((b) => b.drink_type))];
  const sizesForType = data.base_drinks.filter((b) => b.drink_type === drinkType);
  const selectedSize = sizesForType.find((s) => s.id === sizeId);

  const toggleFlavor = (name) => {
    setSelectedFlavors((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]
    );
  };

  const handleAddSpecial = (s) => {
    addItem({
      name: s.name,
      price: s.price,
      drink_type: s.drink_type,
      is_special: true,
      special_id: s.id,
      flavors: [],
    });
    toast.success(`${s.name} added to cart`);
  };

  const handleAddCustom = () => {
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    const name = `${selectedSize.size} ${selectedSize.drink_type}`;
    addItem({
      name,
      price: selectedSize.price,
      drink_type: selectedSize.drink_type,
      size: selectedSize.size,
      flavors: selectedFlavors,
      is_special: false,
    });
    toast.success(`${name} added`);
    setSelectedFlavors([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <CartDrawer />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-24 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
          <p className="text-sm tracking-[0.3em] uppercase text-primary mb-6">Energy · Refreshment · Flow</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight font-light max-w-3xl leading-[1.05]">
            Crafted energy drinks <br /><span className="text-primary italic">made your way.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Build your own Red Bull, Lotus, or Italian Soda with the flavors you love.
            Order ahead for pickup or delivery.
          </p>
          <div className="mt-10 flex gap-3">
            <a href="#specials" data-testid="hero-specials-btn">
              <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-7 tracking-wide">
                See Specials
              </Button>
            </a>
            <a href="#build" data-testid="hero-build-btn">
              <Button variant="outline" className="rounded-full h-12 px-7 border-border tracking-wide">
                Build Your Own
              </Button>
            </a>
          </div>
          </div>
          <div className="hidden md:flex justify-center">
            <img src={LOGO_URL} alt="White River Energy" className="w-full max-w-md drop-shadow-xl" />
          </div>
        </div>
      </section>

      {/* Specials */}
      <section id="specials" className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-sm tracking-[0.3em] uppercase text-primary mb-3">Featured</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">House Specials</h2>
          </div>
          <p className="hidden md:block text-sm text-muted-foreground max-w-xs text-right">
            Our most-loved combinations, ready to order.
          </p>
        </div>

        {loading && <p className="text-muted-foreground">Loading…</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.specials.map((s, idx) => (
            <article
              key={s.id}
              data-testid={`special-card-${s.id}`}
              className="menu-card bg-white rounded-3xl overflow-hidden border border-border/50 animate-fade-up"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="aspect-[4/3] bg-secondary/50 overflow-hidden">
                {s.image_path ? (
                  <img src={fileUrl(s.image_path)} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary to-muted">
                    <span className="font-display text-4xl text-primary/40">{s.drink_type || "✦"}</span>
                  </div>
                )}
              </div>
              <div className="p-6">
                {s.drink_type && (
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">{s.drink_type}</p>
                )}
                <h3 className="font-display text-xl mb-2">{s.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{s.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl text-primary">${s.price.toFixed(2)}</span>
                  <Button
                    data-testid={`add-special-${s.id}`}
                    onClick={() => handleAddSpecial(s)}
                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                  >
                    <Plus className="h-4 w-4" strokeWidth={1.5} /> Add
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Build Your Own */}
      <section id="build" className="bg-secondary/40 border-y border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-primary mb-3">Custom</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">Build Your Own</h2>
            <p className="mt-4 text-muted-foreground max-w-lg">
              Pick a base, choose a size, and add as many flavors as you'd like.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-border/50 p-8 lg:p-12 grid lg:grid-cols-[1fr_1.5fr] gap-12">
            {/* Left: Type & Size */}
            <div className="space-y-10">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Step 1 · Drink Type</p>
                <div className="space-y-2">
                  {drinkTypes.map((t) => (
                    <button
                      key={t}
                      data-testid={`drink-type-${t}`}
                      onClick={() => { setDrinkType(t); setSizeId(null); }}
                      className={`w-full text-left px-5 py-4 rounded-2xl border transition-all font-display text-lg ${
                        drinkType === t
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Step 2 · Size</p>
                <div className="grid grid-cols-2 gap-3">
                  {sizesForType.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`size-${s.id}`}
                      onClick={() => setSizeId(s.id)}
                      className={`px-4 py-5 rounded-2xl border text-center transition-all ${
                        sizeId === s.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className="font-display text-2xl">{s.size}</div>
                      <div className="text-sm text-muted-foreground mt-1">${s.price.toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Flavors */}
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">
                Step 3 · Flavors <span className="normal-case tracking-normal text-muted-foreground/70">(pick as many as you like)</span>
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-2">
                {data.flavors.map((f) => {
                  const checked = selectedFlavors.includes(f.name);
                  return (
                    <label
                      key={f.id}
                      data-testid={`flavor-${f.id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                        checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleFlavor(f.name)}
                        data-testid={`flavor-checkbox-${f.id}`}
                      />
                      <span className="text-sm">{f.name}</span>
                    </label>
                  );
                })}
              </div>

              <div className="mt-8 p-6 rounded-2xl bg-muted/60 border border-border/60">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Your Drink</span>
                  <span className="font-display text-3xl text-primary">
                    ${selectedSize ? selectedSize.price.toFixed(2) : "—"}
                  </span>
                </div>
                <p className="font-display text-lg">
                  {selectedSize ? `${selectedSize.size} ${selectedSize.drink_type}` : "Pick a size to continue"}
                </p>
                {selectedFlavors.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedFlavors.join(", ")}
                  </p>
                )}
                <Button
                  data-testid="add-custom-drink-btn"
                  onClick={handleAddCustom}
                  disabled={!selectedSize}
                  className="w-full mt-5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 font-display tracking-wide gap-2"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} /> Add to Cart
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visit */}
      <section id="visit" className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-3xl border border-border/50 p-8">
            <p className="text-xs tracking-[0.25em] uppercase text-primary mb-3">Pickup</p>
            <h3 className="font-display text-2xl mb-2">Order Ahead</h3>
            <p className="text-sm text-muted-foreground">Ready in 10–15 minutes. Skip the line.</p>
          </div>
          <div className="bg-white rounded-3xl border border-border/50 p-8">
            <p className="text-xs tracking-[0.25em] uppercase text-primary mb-3">Delivery</p>
            <h3 className="font-display text-2xl mb-2">Brought to You</h3>
            <p className="text-sm text-muted-foreground">Local delivery available — fast and fresh.</p>
          </div>
          <div className="bg-primary text-primary-foreground rounded-3xl p-8">
            <p className="text-xs tracking-[0.25em] uppercase opacity-70 mb-3">White River Energy</p>
            <h3 className="font-display text-2xl mb-2">Stop By</h3>
            <p className="text-sm opacity-90">Locally owned. Built for those who keep moving.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/60 py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} White River Energy
      </footer>
    </div>
  );
}
